"use client"
import styled from "@emotion/styled"
import { Button } from "@mui/material"
import { useRef, useState } from "react"
import { useInView } from "react-intersection-observer"
import { Container, Desktop, Mobile, transitionDelayTime, TransitionUp, useInViewConfig } from "ui"
import VideoPauseIcon from "ui/icons/video/VideoPauseIcon"
import VideoPlayIcon from "ui/icons/video/VideoPlayIcon"
import ImageWithAlt from "ui/ImageWithAlt"
import { extractImage, extractProperty } from "utils"
import SeparateForm from "./SeparateForm"

const StyledContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  ${(props) => props.theme.breakpoints.up("md")} {
    grid-template-columns: 1fr 420px;
  }
`

const StyledImage = styled.div`
  position: relative;
  height: 420px;
  width: calc(100vw - 32px);

  margin: 112px 0;
  border-radius: 12px;
  overflow: hidden; /* Add this to hide overflow */
  ${(props) => props.theme.breakpoints.up("md")} {
    width: 420px;
    height: 626px;
  }
`

const StyledButton = styled(Button)`
  position: absolute;
  bottom: 0px;
  right: 0px;
  z-index: 10;
`

const StyledTextAndForm = styled.div``

type Props = {
  data: any
  companyId?: number
}

function BonitaContactForm({ data, companyId }: Props) {
  const desktopImage = extractImage(data, "media")
  const mobileImage = extractProperty(data, "mediaMobile")

  const videoRef = useRef<HTMLVideoElement>(null)
  const videoRefMobile = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false)

  const handlePlay = () => {
    if (videoRef.current) {
      videoRef.current.play()
      setIsPlaying(true)
    }
    if (videoRefMobile.current) {
      videoRefMobile.current.play()
      setIsPlaying(true)
    }
  }

  const handlePause = () => {
    if (videoRef.current) {
      videoRef.current.pause()
      setIsPlaying(false)
    }
    if (videoRefMobile.current) {
      videoRefMobile.current.pause()
      setIsPlaying(false)
    }
  }

  const { ref: mobileRef, inView: mobileInView } = useInView(useInViewConfig)

  return (
    <Container>
      <StyledContainer ref={mobileRef}>
        <StyledTextAndForm>
          <SeparateForm data={data} companyId={companyId} />
        </StyledTextAndForm>
        <TransitionUp $inView={mobileInView} $delay={transitionDelayTime * 3}>
          <Desktop>
            <StyledImage>
              <StyledButton onClick={isPlaying ? handlePause : handlePlay}>
                {isPlaying ? <VideoPauseIcon /> : <VideoPlayIcon />}
              </StyledButton>
              {desktopImage?.url?.endsWith("mp4") ? (
                <video
                  ref={videoRef}
                  muted
                  loop
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover", // This ensures the video covers the container
                  }}
                >
                  <source src={desktopImage?.url} type="video/mp4" />
                </video>
              ) : (
                <ImageWithAlt fill image={desktopImage} />
              )}
            </StyledImage>
          </Desktop>

          <Mobile>
            <StyledImage>
              <StyledButton onClick={isPlaying ? handlePause : handlePlay}>
                {isPlaying ? <VideoPauseIcon /> : <VideoPlayIcon />}
              </StyledButton>
              {mobileImage?.url?.endsWith("mp4") ? (
                <video
                  ref={videoRefMobile}
                  muted
                  loop
                  playsInline
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover", // This ensures the video covers the container
                  }}
                >
                  <source src={mobileImage?.url} type="video/mp4" />
                </video>
              ) : (
                <ImageWithAlt image={mobileImage} fill />
              )}
            </StyledImage>
          </Mobile>
        </TransitionUp>
      </StyledContainer>
    </Container>
  )
}

export default BonitaContactForm
